import {
  Button,
  FormField,
  FormGrid,
  Page,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { Channel } from 'lib-shared-types-channels';
import { useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { AiStyleGuide } from 'apis/ai/types/aiStyleGuides.types';
import { appActions } from 'app/app.actions';
import {
  HookFormSelectionList,
  HookFormText,
  HookFormTextarea,
} from 'components/hookForm';
import { channelAttributes, mediaToChannels } from 'utils/channels';
import { validateName } from 'utils/formValidator';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

const supportedChannels = [
  // All channels in text media
  mediaToChannels['email'],
  mediaToChannels['interactivechat'],
  mediaToChannels['messaging'],
].flat();

const styleGuideMaxLength = 4000;
const styleGuideMinLength = 4;

type SettingsAIOptionsStyleGuideFormProps = {
  styleGuide?: AiStyleGuide;
  onSubmit: SubmitHandler<SettingsAIOptionsStyleGuideFormValues>;
};

export type SettingsAIOptionsStyleGuideFormValues = Omit<
  AiStyleGuide,
  'created_at' | 'id'
>;

export const SettingsAIOptionsStyleGuideForm = ({
  styleGuide,
  onSubmit,
}: SettingsAIOptionsStyleGuideFormProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const showFeedbackModal = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            team: 'conversations',
            title: 'Writing style guide feedback',
          },
        },
      }),
    );
  }, [dispatch]);

  const { handleSubmit, control, formState } =
    useForm<SettingsAIOptionsStyleGuideFormValues>({
      defaultValues: {
        name: styleGuide?.name ?? '',
        contexts: {
          channels: styleGuide?.contexts.channels ?? [],
          features: styleGuide?.contexts.features ?? [],
        },
        content: styleGuide?.content ?? '',
      },
      mode: 'onBlur',
    });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Page.Section
        title="Setup"
        titleMeta={
          <Text.ExternalLink onClick={showFeedbackModal} fontSize="200">
            Send feedback
          </Text.ExternalLink>
        }
        controls={
          <>
            <Button
              isLoading={formState.isSubmitting}
              disabled={!formState.isValid || formState.isSubmitting}
              variant="primary"
              type="submit"
            >
              Save
            </Button>
          </>
        }
      >
        <FormGrid>
          <FormField span={2}>
            <HookFormText
              name="name"
              label="Name"
              control={control}
              rules={{
                required: 'Name is required',
                validate: validateName,
              }}
            />
          </FormField>
          <FormField span={4} />
          <FormField span={2}>
            <HookFormSelectionList
              name="contexts.channels"
              label="Select channels"
              control={control}
              componentProps={{
                multiple: true,
                getItemTitleByValue: (value?: Channel | null) =>
                  (value && channelAttributes[value]?.name) || '',
              }}
            >
              {supportedChannels.map((channelId) => (
                <SelectionList.Item key={channelId} value={channelId}>
                  {channelAttributes[channelId].name}
                </SelectionList.Item>
              ))}
            </HookFormSelectionList>
          </FormField>
          <FormField span={4} />
          <FormField span={4}>
            <HookFormTextarea
              name="content"
              label="Style guide"
              control={control}
              rules={{
                required: 'Writing style guide is required',
                maxLength: {
                  value: styleGuideMaxLength,
                  message: `Reached limit of ${styleGuideMaxLength} characters`,
                },
                minLength: {
                  value: styleGuideMinLength,
                  message: `Must be at least ${styleGuideMinLength} characters long`,
                },
              }}
              componentProps={{
                placeholder:
                  'Develop writing style guide tailored for AI application',
                height: 600,
                style: { resize: 'vertical' },
                showCharacterLimit: styleGuideMaxLength,
              }}
            />
          </FormField>
          <FormField span={4}>
            <Text.Paragraph variant="2">
              If you need inspiration for your writing style guide, have a look
              at our{' '}
              <Text.ExternalLink
                href="https://support.dixa.help/articles/1769-using-writing-style-guides"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  // Normal-click opens the popover
                  // Middle-click opens the link in a new tab
                  event.preventDefault();
                  elevioClient.openArticle('1769');
                }}
              >
                help article
              </Text.ExternalLink>
              .
            </Text.Paragraph>
          </FormField>
        </FormGrid>
      </Page.Section>
    </form>
  );
};
